import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toApiError } from '../lib/api/api-error';
import type { Toast, ToastApi } from '../types/ui';

const ToastContext = createContext<ToastApi | null>(null);

const DURATION_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId.current++;
      setToasts((list) => [...list.slice(-4), { ...toast, id }]);
      window.setTimeout(() => dismiss(id), toast.tone === 'danger' ? DURATION_MS * 2 : DURATION_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push({ tone: 'success', message }),
      info: (message) => push({ tone: 'info', message }),
      warning: (message) => push({ tone: 'warning', message }),
      error: (error, fallback) => {
        const apiError = toApiError(error);
        push({
          tone: 'danger',
          message: apiError.message || fallback || 'Ocorreu um erro.',
          correlationId: apiError.isUnexpected ? apiError.correlationId : null,
        });
      },
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-region" aria-live="polite" aria-relevant="additions">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast toast--${toast.tone}`}
            role={toast.tone === 'danger' ? 'alert' : 'status'}
          >
            <div className="toast__content">
              <p className="toast__message">{toast.message}</p>
              {toast.correlationId && (
                <p className="support-code">Código para suporte: {toast.correlationId}</p>
              )}
            </div>
            <button
              type="button"
              className="btn btn--ghost btn--icon btn--sm"
              aria-label="Fechar aviso"
              onClick={() => dismiss(toast.id)}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast deve ser usado dentro de <ToastProvider>.');
  return ctx;
}
